/**
 * @file Shared helper for presentation modes (Slideshow, Virtual Field Trip)
 */
import type { Page, PresentationMode, PresentationPage } from '@@/types/presentation'
import { PresentationPageType } from '@@/types/presentation'

/**
 * Reads the current `window.location.search` and merges it with optional `overrides`.
 * Overrides win on key clashes (e.g. `lang` when viewing a translated padlet).
 */
const getPresentationSearchParams = (overrides?: Record<string, string>): Record<string, string> | undefined => {
  const currentParams = new URLSearchParams(window.location.search)
  const merged: Record<string, string> = { ...Object.fromEntries(currentParams), ...overrides }
  return Object.keys(merged).length > 0 ? merged : undefined
}

const buildPresentationLink = ({
  showLink,
  page,
  searchParams,
  mode = 'slideshow',
}: {
  showLink: string
  page: PresentationPage
  searchParams?: ConstructorParameters<typeof URLSearchParams>[0]
  mode?: PresentationMode
}): string => {
  const pageUrlSuffix = ((): string => {
    switch (page.pageType) {
      case PresentationPageType.Section:
        return `/section/${page.sectionId}`
      case PresentationPageType.Group:
        return `/group/${page.groupId}`
      case PresentationPageType.Wish:
        return `/wish/${page.wishId}`
      case PresentationPageType.Cover:
      default:
        return ''
    }
  })()

  const normalizedShowLink = showLink.replace(/\/+$/, '')
  const baseUrl = new URL(`${normalizedShowLink}/${mode}${pageUrlSuffix}`)
  if (searchParams != null) {
    for (const [key, value] of new URLSearchParams(searchParams)) {
      baseUrl.searchParams.set(key, value)
    }
  }
  return baseUrl.toString()
}

// Index of the cover page in the flat page list.
const COVER_PAGE_INDEX = 0

// Half the duration of the pinhole (iris) page transition animation.
// The full animation is close → open; each half lasts this long.
const PINHOLE_HALF_DURATION_MS = 750

/**
 * Parse the current URL to find the matching page index in the pages list.
 * Supports `/field-trip`, `/field-trip/section/:id`, `/field-trip/wish/:id`.
 * Returns COVER_PAGE_INDEX when the URL doesn't match any page.
 */
const resolvePageIndexFromUrl = (pages: Page[]): number => {
  const match = window.location.pathname.match(/\/field-trip(?:\/(section|wish)\/(\d+))?/)
  if (match == null) return COVER_PAGE_INDEX

  const [, type, id] = match
  if (type == null || id == null) return COVER_PAGE_INDEX

  const targetPageType = type === 'section' ? PresentationPageType.Section : PresentationPageType.Wish
  const index = pages.findIndex((page) => {
    if (targetPageType === PresentationPageType.Section) {
      return (
        (page.pageType === PresentationPageType.Section || page.pageType === PresentationPageType.Group) &&
        page.entityId === id
      )
    }
    return page.pageType === PresentationPageType.Wish && page.entityId === id
  })

  return index >= 0 ? index : COVER_PAGE_INDEX
}

// Convert a post's entity ID to its CID (content identifier).
const getCurrentPostCid = (entityId: string): string => `c${entityId}`

export {
  buildPresentationLink,
  COVER_PAGE_INDEX,
  getCurrentPostCid,
  getPresentationSearchParams,
  PINHOLE_HALF_DURATION_MS,
  resolvePageIndexFromUrl,
}
export type { PresentationMode, PresentationPage }
export { PresentationPageType }
